import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, User, MessageCircle } from 'lucide-react';
import AIChatbot from '@/components/AIChatbot';
import { getPublicApiUrl } from '@/lib/publicApi';

const API_URL = getPublicApiUrl();

// Function to inject tracking scripts
const injectTrackingScripts = (settings) => {
  // Google Tag Manager
  if (settings?.google_tag_manager_id && !document.getElementById('gtm-injected')) {
    const gtmId = settings.google_tag_manager_id;
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-injected';
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(gtmScript);
    if (!document.getElementById('gtm-noscript')) {
      const noscript = document.createElement('noscript');
      noscript.id = 'gtm-noscript';
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }

  // Google Analytics 4 + Google Ads (via gtag.js - unified)
  const gaId = settings?.google_analytics_id;
  const gadsId = settings?.google_ads_id;
  if ((gaId || gadsId) && !document.getElementById('gtag-injected')) {
    // Use GA4 ID for the main gtag.js src, fallback to Ads ID
    const primaryId = gaId || gadsId;
    const gtagScript = document.createElement('script');
    gtagScript.id = 'gtag-injected';
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
    document.head.appendChild(gtagScript);

    let configLines = `window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());`;
    if (gaId) configLines += `\n      gtag('config', '${gaId}');`;
    if (gadsId) configLines += `\n      gtag('config', '${gadsId}');`;

    const gtagConfig = document.createElement('script');
    gtagConfig.id = 'gtag-config';
    gtagConfig.innerHTML = configLines;
    document.head.appendChild(gtagConfig);
  }

  // Meta Pixel (Facebook)
  const fbPixelId = settings?.facebook_pixel_id || settings?.meta_pixel_id;
  if (fbPixelId && !document.getElementById('meta-pixel-injected')) {
    const metaScript = document.createElement('script');
    metaScript.id = 'meta-pixel-injected';
    metaScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${fbPixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(metaScript);
    const metaNoscript = document.createElement('noscript');
    metaNoscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${fbPixelId}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(metaNoscript);
  }

  // Custom head scripts
  if (settings?.custom_head_scripts && !document.getElementById('custom-head-scripts')) {
    const customHead = document.createElement('div');
    customHead.id = 'custom-head-scripts';
    customHead.style.display = 'none';
    customHead.innerHTML = settings.custom_head_scripts;
    // Execute scripts from custom_head_scripts
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = settings.custom_head_scripts;
    tempDiv.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      document.head.appendChild(newScript);
    });
    document.head.appendChild(customHead);
  }

  // Custom body scripts
  if (settings?.custom_body_scripts && !document.getElementById('custom-body-scripts')) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = settings.custom_body_scripts;
    tempDiv.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      document.body.appendChild(newScript);
    });
    const marker = document.createElement('div');
    marker.id = 'custom-body-scripts';
    marker.style.display = 'none';
    document.body.appendChild(marker);
  }
};

export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showContactBar, setShowContactBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null); // 'produits' | 'outils' | null
  const location = useLocation();

  useEffect(() => {
    fetchSettings();
    fetchPages();
  }, []);

  // Hide contact bar on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowContactBar(false);
      } else {
        setShowContactBar(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Inject tracking scripts when settings are loaded
  useEffect(() => {
    if (settings) {
      injectTrackingScripts(settings);
    }
  }, [settings]);

  // Update document title based on page
  useEffect(() => {
    const pageTitles = {
      '/portail': 'Accueil - IDMAT OI',
      '/portail/machines': 'Nos Machines - IDMAT OI',
      '/portail/configurateur': 'Devis Gratuit - IDMAT OI',
      '/portail/espace-client': 'Espace Client - IDMAT OI',
    };
    document.title = pageTitles[location.pathname] || 'IDMAT OI - Engins de Chantier à La Réunion';
  }, [location.pathname]);

  // Track SPA page changes for GA4 and Facebook Pixel
  useEffect(() => {
    if (!settings) return;
    // GA4 pageview on route change
    if (settings.google_analytics_id && window.gtag) {
      window.gtag('config', settings.google_analytics_id, {
        page_path: location.pathname,
        page_title: document.title,
      });
    }
    // Facebook Pixel pageview on route change
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, settings]);

  // Track page views (analytics intégré)
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get or create visitor ID
        let visitorId = localStorage.getItem('idmat_visitor_id');
        if (!visitorId) {
          visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
          localStorage.setItem('idmat_visitor_id', visitorId);
        }
        
        // Get or create session ID
        let sessionData = sessionStorage.getItem('idmat_session');
        let sessionId;
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
            sessionId = parsed.id;
            sessionStorage.setItem('idmat_session', JSON.stringify({ id: sessionId, timestamp: Date.now() }));
          }
        }
        if (!sessionId) {
          sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
          sessionStorage.setItem('idmat_session', JSON.stringify({ id: sessionId, timestamp: Date.now() }));
        }
        
        await fetch(`${API_URL}/api/public/analytics/track/pageview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page_path: location.pathname,
            page_title: document.title,
            referrer: document.referrer || null,
            visitor_id: visitorId,
            session_id: sessionId
          }),
          keepalive: true
        });
      } catch (e) {
        // Silent fail
      }
    };
    
    trackPageView();
  }, [location.pathname]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/settings`);
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchPages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/pages`);
      const data = await res.json();
      setPages(data.filter(p => p.show_in_menu));
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    }
  };

  const primaryColor = settings?.primary_color || '#f59e0b';
  const secondaryColor = settings?.secondary_color || '#1e293b';

  return (
    <div className="min-h-screen flex flex-col" style={{ '--primary': primaryColor, '--secondary': secondaryColor }}>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 px-4 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${settings?.contact_phone || '0693526449'}`} className="flex items-center gap-2 hover:text-yellow-400 transition">
              <Phone className="w-4 h-4" />
              {settings?.contact_phone || '0693 52 64 49'}
            </a>
            <a href={`mailto:${settings?.contact_email || 'contact@idmatoi.com'}`} className="flex items-center gap-2 hover:text-yellow-400 transition">
              <Mail className="w-4 h-4" />
              {settings?.contact_email || 'contact@idmatoi.com'}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {settings?.contact_address || 'La Réunion'}
          </div>
        </div>
      </div>

      {/* Main Header - Style idmatoi.com */}
      <header className="bg-white sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/portail" className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={settings?.site_name} className="h-12" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">ID</span>
                  </div>
                  <div>
                    <span className="font-bold text-xl text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                      {settings?.site_name || 'IDMAT OI'}
                    </span>
                    <p className="text-xs text-slate-500">{settings?.site_tagline || 'Mini-Pelles Professionnelles'}</p>
                  </div>
                </div>
              )}
            </Link>

            {/* Desktop Navigation - Ordre identique à idmatoi.com */}
            <nav className="hidden lg:flex items-center gap-6">
              <Link 
                to="/portail" 
                className={`font-medium transition ${location.pathname === '/portail' ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
              >
                Accueil
              </Link>

              {/* ===== Menu déroulant PRODUITS ===== */}
              <div
                className="relative"
                onMouseEnter={() => setOpenDropdown('produits')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className={`flex items-center gap-1.5 font-medium transition ${
                    location.pathname.includes('/machines') || location.pathname.includes('/accessoires')
                      ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'
                  }`}
                >
                  Produits
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'produits' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'produits' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-52 bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                      <Link
                        to="/portail/machines"
                        onClick={() => setOpenDropdown(null)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-yellow-50 hover:text-yellow-600 ${location.pathname.includes('/machines') ? 'text-yellow-500 font-medium bg-yellow-50' : 'text-slate-700'}`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base">🚜</span>
                        <div>
                          <p className="font-medium">Machines</p>
                          <p className="text-xs text-slate-400">Mini-pelles neuves</p>
                        </div>
                      </Link>
                      <Link
                        to="/portail/accessoires"
                        onClick={() => setOpenDropdown(null)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-yellow-50 hover:text-yellow-600 ${location.pathname.includes('/accessoires') ? 'text-yellow-500 font-medium bg-yellow-50' : 'text-slate-700'}`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base">🔧</span>
                        <div>
                          <p className="font-medium">Accessoires</p>
                          <p className="text-xs text-slate-400">Godets, brise-roches…</p>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/portail/parts"
                className={`font-medium transition ${location.pathname.includes('/parts') ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
              >
                Parts
              </Link>

              {/* ===== Menu déroulant OUTILS ===== */}
              <div
                className="relative"
                onMouseEnter={() => setOpenDropdown('outils')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className={`flex items-center gap-1.5 font-medium transition ${
                    location.pathname.includes('/configurateur') || location.pathname.includes('/financement')
                      ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'
                  }`}
                >
                  Outils
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'outils' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'outils' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                      <Link
                        to="/portail/configurateur"
                        onClick={() => setOpenDropdown(null)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-yellow-50 hover:text-yellow-600 ${location.pathname.includes('/configurateur') ? 'text-yellow-500 font-medium bg-yellow-50' : 'text-slate-700'}`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base">⚙️</span>
                        <div>
                          <p className="font-medium">Configurateur</p>
                          <p className="text-xs text-slate-400">Personnalisez votre machine</p>
                        </div>
                      </Link>
                      <Link
                        to="/portail/financement"
                        onClick={() => setOpenDropdown(null)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-yellow-50 hover:text-yellow-600 ${location.pathname.includes('/financement') ? 'text-yellow-500 font-medium bg-yellow-50' : 'text-slate-700'}`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base">💶</span>
                        <div>
                          <p className="font-medium">Mon Financement</p>
                          <p className="text-xs text-slate-400">Simulation & dossier</p>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link 
                to="/portail/blog" 
                className={`font-medium transition ${location.pathname.includes('/blog') ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
              >
                Blog
              </Link>
              <Link 
                to="/portail/mon-compte" 
                className={`font-medium transition flex items-center gap-1 ${location.pathname.includes('/mon-compte') ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
              >
                <User className="w-4 h-4" />
                Mon Compte
              </Link>
              {pages.map(page => (
                <Link 
                  key={page.id}
                  to={`/portail/${page.slug}`}
                  className={`font-medium transition ${location.pathname.includes(page.slug) ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
                >
                  {page.title}
                </Link>
              ))}
              <Link 
                to="/portail/contact" 
                className={`font-medium transition ${location.pathname.includes('/contact') ? 'text-yellow-500' : 'text-slate-700 hover:text-yellow-500'}`}
              >
                Contact
              </Link>
            </nav>

            {/* CTA Button - Style idmatoi.com */}
            <div className="hidden lg:flex items-center gap-4">
              <Link 
                to="/portail/devis"
                className="px-6 py-3 bg-yellow-500 text-white font-semibold rounded-xl hover:bg-yellow-600 transition shadow-lg shadow-yellow-500/30"
              >
                Demander un devis
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <nav className="flex flex-col p-4 gap-1">
              <Link to="/portail" className="py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                Accueil
              </Link>

              {/* Produits (accordéon) */}
              <div>
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'produits' ? null : 'produits')}
                  className="w-full flex items-center justify-between py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Produits
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'produits' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'produits' && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-yellow-200 pl-3">
                    <Link to="/portail/machines" className="py-2 block text-sm text-slate-600 hover:text-yellow-600" onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}>
                      🚜 Machines
                    </Link>
                    <Link to="/portail/accessoires" className="py-2 block text-sm text-slate-600 hover:text-yellow-600" onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}>
                      🔧 Accessoires
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/portail/parts" className="py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                Parts
              </Link>

              {/* Outils (accordéon) */}
              <div>
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'outils' ? null : 'outils')}
                  className="w-full flex items-center justify-between py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Outils
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'outils' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'outils' && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-yellow-200 pl-3">
                    <Link to="/portail/configurateur" className="py-2 block text-sm text-slate-600 hover:text-yellow-600" onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}>
                      ⚙️ Configurateur
                    </Link>
                    <Link to="/portail/financement" className="py-2 block text-sm text-slate-600 hover:text-yellow-600" onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}>
                      💶 Mon Financement
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/portail/blog" className="py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                Blog
              </Link>
              <Link to="/portail/mon-compte" className="py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <User className="w-4 h-4" /> Mon Compte
              </Link>
              <Link to="/portail/contact" className="py-2.5 px-3 font-medium text-slate-700 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </Link>
              <Link 
                to="/portail/devis"
                className="mt-2 py-3 bg-yellow-500 text-white font-semibold rounded-xl text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Demander un devis
              </Link>
              <a href={`tel:${settings?.contact_phone}`} className="py-2 px-3 text-slate-600 flex items-center gap-2">
                <Phone className="w-4 h-4" /> {settings?.contact_phone || '0693 52 64 49'}
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet context={{ settings }} />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        {/* Section À propos - IDMAT OI */}
        <div className="border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              {/* Contenu À propos */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  À propos de nous
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  IDMAT OI, une jeune société avec{' '}
                  <span className="text-yellow-500">20 ans d'expérience</span>
                </h3>
                <p className="text-slate-300 mb-6 max-w-2xl">
                  Forts de <span className="text-yellow-400 font-semibold">20 années d'expertise</span> dans le domaine des engins de chantier, 
                  notre équipe a accompagné des centaines de projets à La Réunion : <span className="text-white font-medium">location d'engins, 
                  VRD, terrassement, travaux publics</span>... Aujourd'hui, nous mettons ce savoir-faire au service de l'importation de 
                  mini-pelles professionnelles pour vous offrir les meilleures machines au meilleur prix.
                </p>
                
                {/* Points clés */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
                  {[
                    { icon: '🏗️', text: 'Location d\'engins' },
                    { icon: '🛤️', text: 'VRD & Terrassement' },
                    { icon: '🔧', text: 'SAV expert' },
                    { icon: '🏝️', text: '100% Réunion' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full">
                      <span>{item.icon}</span>
                      <span className="text-sm text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* CTA Boutons */}
              <div className="flex flex-col gap-3">
                <Link 
                  to="/portail/histoire-liugong"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-yellow-500/30 transition-all"
                >
                  🏛️ Découvrir notre histoire
                </Link>
                <Link 
                  to="/portail/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 text-white font-medium rounded-xl hover:bg-slate-600 transition"
                >
                  📞 Nous contacter
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer principal */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">ID</span>
                </div>
                <span className="font-bold text-xl">{settings?.site_name || 'IDMAT OI'}</span>
              </div>
              <p className="text-slate-400 mb-4">
                Importateur de mini-pelles à La Réunion. Des machines fiables, puissantes et accessibles pour particuliers et professionnels.
              </p>
              <div className="flex gap-4">
                {settings?.facebook_url && (
                  <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-yellow-500 transition">
                    Facebook
                  </a>
                )}
                {settings?.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-yellow-500 transition">
                    Instagram
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-yellow-500">Navigation</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/portail" className="hover:text-white transition">Accueil</Link></li>
                <li><Link to="/portail/machines" className="hover:text-white transition">Machines</Link></li>
                <li><Link to="/portail/accessoires" className="hover:text-white transition">Accessoires</Link></li>
                <li><Link to="/portail/parts" className="hover:text-white transition">Parts</Link></li>
                <li><Link to="/portail/configurateur" className="hover:text-white transition">Configurateur</Link></li>
                <li><Link to="/portail/financement" className="hover:text-white transition">Mon Financement</Link></li>
                <li><Link to="/portail/blog" className="hover:text-white transition">Blog</Link></li>
                <li><Link to="/portail/contact" className="hover:text-white transition">Contact</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-yellow-500">Contact</h4>
              <ul className="space-y-3 text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-yellow-500" />
                  <a href={`tel:${settings?.contact_phone}`} className="hover:text-white transition">
                    {settings?.contact_phone || '0693 52 64 49'}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-yellow-500" />
                  <a href={`mailto:${settings?.contact_email}`} className="hover:text-white transition">
                    {settings?.contact_email || 'contact@idmatoi.com'}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-yellow-500" />
                  <span>{settings?.contact_address || 'La Réunion'}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {settings?.site_name || 'IDMAT OI'}. Tous droits réservés.
            </p>
            <Link 
              to="/admin/login" 
              className="text-slate-500 hover:text-yellow-500 text-xs transition"
            >
              Espace collaborateurs →
            </Link>
          </div>
        </div>
      </footer>

      {/* AI Chatbot */}
      <AIChatbot />

      {/* Mobile Floating Contact Button - Small and discrete */}
      <div 
        className={`fixed right-4 z-40 md:hidden transition-all duration-300 ${
          showContactBar ? 'bottom-4 opacity-100' : '-bottom-20 opacity-0'
        }`}
      >
        <a 
          href={`tel:${settings?.contact_phone || '0693526449'}`}
          className="flex items-center justify-center w-12 h-12 bg-yellow-500 text-white rounded-full shadow-lg hover:bg-yellow-600 transition"
          aria-label="Appeler"
        >
          <Phone className="w-5 h-5" />
        </a>
      </div>

      {/* Desktop Floating Contact Button */}
      <div className="hidden md:flex fixed bottom-24 right-6 z-40 flex-col gap-3">
        <a 
          href={`tel:${settings?.contact_phone || '0693526449'}`}
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-3 rounded-full shadow-lg hover:bg-slate-800 transition group"
        >
          <Phone className="w-5 h-5 text-yellow-400" />
          <span className="font-medium">{settings?.contact_phone || '0693 52 64 49'}</span>
        </a>
        <a 
          href={`https://wa.me/${(settings?.contact_phone || '0693526449').replace(/\s/g, '').replace(/^0/, '262')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-green-700 transition"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">WhatsApp</span>
        </a>
      </div>
    </div>
  );
}
